/**
 * Sessions Resource
 *
 * Thin 1:1 mapping to the sessions API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import { SDKError } from '../errors.js';
import type { HttpLayer } from '../http.js';
import type {
  Session,
  CreateSessionRequest,
  UpdateSessionRequest,
  Message,
  CreateMessageRequest,
  SessionEvent,
  SubscribeSessionEventsRequest,
  UnsubscribeSessionEventsRequest,
  EventPayloadPage,
  EventPayloadWindowEntry,
  ReadEventPayloadsRequest,
  HydratedEventData,
  HydratedEventWindowEntry,
  Checkpoint,
  SessionImageUpload,
  UserConfig,
  UpdateUserConfigRequest,
} from '../types/index.js';

// `offset_bytes` is a BYTE offset: the server slices the event's `data` TEXT
// rune-safely and echoes the byte-start it used. The paged-read cursor must
// advance by the UTF-8 BYTE length of each returned fragment — NOT its string
// `.length`, which counts UTF-16 code units and desyncs the offset the moment a
// payload contains any multibyte content (emoji, non-Latin text), mis-slicing
// every subsequent page.
const payloadByteLength = (s: string): number => new TextEncoder().encode(s).length;

export class SessionsResource {
  constructor(private http: HttpLayer) {}

  private prefix(workspaceId?: string | null): string {
    return workspaceId ? `/v1/sessions/${workspaceId}` : `/v1/sessions`;
  }

  // Image upload/fetch live on the reasoning routes, beside send-message.
  private reasoningPrefix(workspaceId?: string | null): string {
    return workspaceId ? `/v1/reasoning/${workspaceId}` : `/v1/reasoning`;
  }

  // --- Sessions ---

  async create(
    workspaceId: string | null | undefined,
    params: CreateSessionRequest,
  ): Promise<Session> {
    return this.http.post(`${this.prefix(workspaceId)}/sessions`, params);
  }

  /**
   * Sessions for this lane, most recent transcript message first.
   *
   * The route is BOUNDED: `limit` defaults to 200 server-side and caps at 500,
   * and ordering is `(last_accessed DESC, id DESC)` so `offset` is stable
   * across pages. The response stays a bare array, so to know whether the bound
   * cut anything off, ask for one more row than you intend to render — a
   * returned length greater than your page size is the signal, and is exact at
   * the boundary in a way `length === limit` is not.
   *
   * @param workspaceId Workspace to scope to, or null/undefined for the
   *   account-wide list (both lanes).
   * @param params Page bounds: `limit` (1-500) and `offset` (>= 0).
   */
  async list(
    workspaceId: string | null | undefined,
    params?: { limit?: number; offset?: number },
  ): Promise<Session[]> {
    return this.http.get(`${this.prefix(workspaceId)}/sessions`, params);
  }

  async get(workspaceId: string | null | undefined, sessionId: string): Promise<Session> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
    );
  }

  async getMostRecent(workspaceId: string | null | undefined): Promise<Session> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/most-recent`,
    );
  }

  async update(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UpdateSessionRequest,
  ): Promise<Session> {
    return this.http.patch(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
      params,
    );
  }

  async delete(workspaceId: string | null | undefined, sessionId: string): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
    );
  }

  // --- Messages ---

  async createMessage(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: CreateMessageRequest,
  ): Promise<Message> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
      params,
    );
  }

  async getMessages(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Message[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
    );
  }

  /**
   * Keyset-paged transcript read. Exactly one window mode per call:
   *
   *   - `{ latest: true, limit }` — the newest `limit` visible messages,
   *     ascending (the tail page).
   *   - `{ beforeMessageIndex, limit }` — up to `limit` visible messages
   *     below `beforeMessageIndex`, ascending (backward extension).
   *   - `{ afterMessageIndex, limit }` — up to `limit` visible messages
   *     above `afterMessageIndex`, ascending (live-tail catch-up).
   *
   * Every row carries `message_index` — the lane's own monotonic cursor the
   * caller forms the next page from. Windowing params require `limit > 0`
   * (422 otherwise); the unparameterized {@link getMessages} stays the
   * byte-compatible full read.
   */
  async getMessagesPage(
    workspaceId: string | null | undefined,
    sessionId: string,
    opts:
      | { latest: true; limit: number }
      | { beforeMessageIndex: number; limit: number }
      | { afterMessageIndex: number; limit: number },
  ): Promise<Message[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
      {
        latest: 'latest' in opts ? opts.latest : undefined,
        before_message_index:
          'beforeMessageIndex' in opts ? opts.beforeMessageIndex : undefined,
        after_message_index:
          'afterMessageIndex' in opts ? opts.afterMessageIndex : undefined,
        limit: opts.limit,
      },
    );
  }

  async clearMessages(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
    );
  }

  async editMessageAndRewind(
    workspaceId: string | null | undefined,
    sessionId: string,
    messageId: string,
    content: string,
  ): Promise<Message[]> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages/${messageId}/edit-and-rewind`,
      { content },
    );
  }

  // --- Images ---

  /**
   * Upload one image to a session and get back its `image://` handle.
   *
   * The bytes go up as the request body under the blob's own content type — the
   * server SNIFFS them, so a declared type is advisory and a lying one is
   * corrected in the response. Server-side the upload refuses a non-image
   * (415), an animated GIF (415), a body over the 5 MiB per-image cap (413), a
   * header declaring more than 40 megapixels (413), and anything past the
   * session's storage ceilings (413); it strips EXIF/GPS/colour profiles by
   * re-encoding into the read-quality copy the vision model reads.
   *
   * Pass the returned `image_id` to `ChatSession.send` as
   * `{ type: 'image', image_id, label }` to attach it to a message. Nothing
   * image-shaped is persisted: the read-quality copy expires from the cache,
   * and the message keeps a text placeholder.
   *
   * The routes live under the reasoning prefix, not the sessions prefix,
   * because attachment binding is part of the send path.
   *
   * @param sessionId Session to attach the image to.
   * @param bytes The image itself (a File, a Blob, or raw bytes).
   * @param label User-facing name; neutralized and length-capped server-side.
   * @param workspaceId Workspace for a workspace-attached session; omit for a
   *   workspace-less (web) session.
   * @param source How the user added it — `paste` or `upload`.
   */
  async uploadImage(
    sessionId: string,
    bytes: Blob,
    label: string,
    workspaceId?: string | null,
    source?: 'paste' | 'upload',
  ): Promise<SessionImageUpload> {
    if (bytes.type === '') {
      throw new SDKError(
        'uploadImage: the blob carries no content type — construct it with an explicit image/* type',
      );
    }
    return this.http.postBinary<SessionImageUpload>(
      `${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/images`,
      bytes,
      bytes.type,
      {
        // Percent-encoded so a Unicode filename survives an ASCII header and a
        // CR/LF the server's neutralizer must strip is expressible at all.
        'X-Image-Label': encodeURIComponent(label),
        'X-Image-Source': source ?? 'upload',
      },
    );
  }

  // --- Checkpoints ---

  async listCheckpoints(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Checkpoint[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints`,
    );
  }

  async createCheckpoint(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Checkpoint> {
    // Send an empty body — the create-checkpoint route declares a (required)
    // body whose only field (agent_state) is optional; without a body Huma
    // rejects with "request body is required".
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints`,
      {},
    );
  }

  async getCheckpoint(
    workspaceId: string | null | undefined,
    checkpointId: string,
  ): Promise<Checkpoint> {
    return this.http.get(
      `${this.prefix(workspaceId)}/checkpoints/${checkpointId}`,
    );
  }

  async deleteCheckpoint(
    workspaceId: string | null | undefined,
    checkpointId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/checkpoints/${checkpointId}`,
    );
  }

  async restoreCheckpoint(
    workspaceId: string | null | undefined,
    sessionId: string,
    checkpointId: string,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints/${checkpointId}/restore`,
    );
  }

  // --- User Config ---

  async getUserConfig(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<UserConfig> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/user-config`,
    );
  }

  async updateUserConfig(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UpdateUserConfigRequest,
  ): Promise<UserConfig> {
    return this.http.patch(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/user-config`,
      params,
    );
  }

  async getUserConfigById(
    workspaceId: string | null | undefined,
    userConfigId: string,
  ): Promise<UserConfig> {
    return this.http.get(
      `${this.prefix(workspaceId)}/user-configs/${userConfigId}`,
    );
  }

  // --- Events ---

  async getEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    afterSequence?: number,
    limit?: number,
    opts?: {
      /** Newest `limit` events, ascending. Combines with neither cursor. */
      latest?: boolean;
      /** The `limit` events immediately below this sequence, ascending. */
      beforeSequence?: number;
      /** Heavy-stripped rows with handle + size_bytes above the inline threshold. */
      skeleton?: boolean;
    },
  ): Promise<SessionEvent[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/events`,
      {
        after_sequence: afterSequence,
        limit,
        latest: opts?.latest,
        before_sequence: opts?.beforeSequence,
        skeleton: opts?.skeleton,
      },
    );
  }

  // --- Lazy event-payload hydration ---

  /**
   * Hydrate ONE skeletonized event's full `data`, looping `has_more` to
   * completion so the returned object is the COMPLETE, byte-identical
   * event data — never a partial page. The server pages the event's `data` TEXT
   * rune-safely; this reassembles the fragments and JSON-parses the whole.
   *
   * Rejects loudly on a non-2xx (404 → the event is no longer available in the
   * caller's lane; the caller maps that to hydrationError='not_available').
   * A page whose reassembled text fails to parse is a loud error, not a silent
   * empty (no-fallbacks).
   */
  async readEventPayload(
    workspaceId: string | null | undefined,
    sessionId: string,
    sequenceNum: number,
    maxBytes?: number,
  ): Promise<HydratedEventData> {
    const base = `${this.prefix(workspaceId)}/sessions/${sessionId}/events/${sequenceNum}/payload`;
    let text = '';
    let offset = 0;
    // Loop pages until the server reports the payload is fully drained. The
    // server bounds forward progress (each page returns >= 1 byte until the
    // end), so this terminates.
    for (;;) {
      const page: EventPayloadPage = await this.http.get(base, {
        offset_bytes: offset,
        max_bytes: maxBytes,
      });
      text += page.content;
      if (!page.has_more) break;
      const next = page.offset_bytes + payloadByteLength(page.content);
      if (next <= offset) {
        // Defensive: no forward progress would loop forever — fail loudly
        // rather than hang (no-fallbacks / no silent truncation).
        throw new Error(
          `readEventPayload: no forward progress at offset ${offset} (seq ${sequenceNum})`,
        );
      }
      offset = next;
    }
    return JSON.parse(text) as HydratedEventData;
  }

  /**
   * Hydrate a WINDOW of events in one request (one server query). Each
   * returned entry is a byte-bounded first page: a COMPLETE entry (`has_more`
   * false) is parsed to `data`; an incomplete one carries `data: null` +
   * `has_more: true` and the caller completes it with {@link readEventPayload}.
   * `sequenceNums` is server-capped — an over-cap request rejects 422.
   */
  async readEventPayloads(
    workspaceId: string | null | undefined,
    sessionId: string,
    sequenceNums: number[],
    maxBytes?: number,
  ): Promise<HydratedEventWindowEntry[]> {
    const body: ReadEventPayloadsRequest = { sequence_nums: sequenceNums, max_bytes: maxBytes };
    const res: { payloads: EventPayloadWindowEntry[] } = await this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/events/payloads`,
      body,
    );
    return res.payloads.map((p) => ({
      sequence_num: p.sequence_num,
      data: p.has_more ? null : (JSON.parse(p.content) as HydratedEventData),
      has_more: p.has_more,
    }));
  }

  // --- Session-event subscription ---

  /**
   * Register the caller's live bridge connection as a `SessionScope` fanout
   * target for `sessionId` and trigger a replay of persisted events after
   * `params.after_sequence`. Server-managed subscription (no client-sent bridge
   * frame, ADR-003): this HTTP action writes the `bridge_subscriptions` row as a
   * side effect. Rejects loudly on a non-2xx (403 device-ownership / 404 session /
   * 422 bad cursor). Workspace-scoped when a workspaceId is supplied, else the
   * workspace-less sibling route.
   */
  async subscribeSessionEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: SubscribeSessionEventsRequest,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/subscribe`,
      params,
    );
  }

  /**
   * Delete the `(device_id, "session", sessionId)` subscription row. Runs the
   * same session-authz + device-ownership checks as {@link subscribeSessionEvents}
   * (a caller may only unsubscribe a device that is provably their own live
   * connection). Rejects loudly on a non-2xx.
   */
  async unsubscribeSessionEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UnsubscribeSessionEventsRequest,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/unsubscribe`,
      params,
    );
  }
}
